// ============================================================================
// PollsDock.tsx
//
// Sondages en direct (2026-10-15), côté réalisateur : préparer des sondages de
// 2 à 4 réponses, en lancer un, suivre les votes des spectateurs de Kleon.tv,
// le clore, et choisir de le montrer à l'antenne.
//
// Comme le bouton « Public » (StageRequestsDock), un bouton dans l'en-tête de
// l'onglet Régie, à côté du nom de la chaîne, qui ouvre le panneau : on lance
// un sondage pendant le direct, sans quitter la régie. Le panneau reprend les
// trois colonnes de la maquette (canevas « Sondages en direct — Kleon »,
// planche 4) : Préparés, Modifier le sondage, En cours.
//
// « Afficher à l'antenne » : le panneau écrit le bandeau dans `overlayRef`, que
// la boucle de composition (useCanvasPipeline → pollOverlay.ts) dessine sur
// l'image diffusée. Le choix est gardé par le serveur (Poll.onAir) : une régie
// rechargée le retrouve.
//
// Règles (validées le 2026-10-15) : chaînes créées par la régie web seulement ;
// un seul sondage ouvert à la fois ; une voix par compte, définitive ; résultat
// montré 30 s après la clôture ; mode quiz (une bonne réponse, révélée à la
// clôture).
//
// Démo sans serveur : `?demoSondage` dans l'adresse de la régie.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, Check, Copy, Pencil, Plus, Radio, Trash2, X } from 'lucide-react';

import {
  Poll, PollDraftInput, PollError, PollsState,
  closePoll, createPoll, deletePoll, duplicatePoll, fetchPolls, openPoll, setPollOnAir, updatePoll, withdrawPoll,
} from '../../../backendServices/pollManagment/api_polls';
import { PollOverlay } from './pollOverlay';
import {
  STREAM_DECK_POLL_DRAFTS, StreamDeckPollCommand, publishStreamDeckPolls, setStreamDeckPollsHandler,
} from '../../../dataTypes/liveStreamManagment/streamDeckBridge';

interface Props {
  adminId          : number | undefined;
  channelId        : number | undefined;
  /** Faux pour une chaîne créée par les apps : le bouton ne paraît pas. */
  createdFromRegie : boolean;
  /** Bandeau à l'antenne, lu par la boucle de composition. */
  overlayRef       : React.MutableRefObject<PollOverlay | null>;
  showAlert        : (message: string, type?: 'important') => void;
}

/** 2 s pendant un sondage (les barres bougent), 5 s sinon, 20 s onglet caché sans bandeau. */
const POLL_LIVE_MS    = 2_000;
const POLL_IDLE_MS    = 5_000;
const POLL_HIDDEN_MS  = 20_000;

const DURATIONS: { label: string; value: number | null }[] = [
  { label: '1 min',       value: 60  },
  { label: '2 min',       value: 120 },
  { label: '5 min',       value: 300 },
  { label: 'Sans limite', value: null },
];

const QUESTION_MAX = 120;
const OPTION_MAX   = 60;

const AIR_ON_LAUNCH_KEY = 'kleon.polls.airOnLaunch';

const DEMO = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('demoSondage');

// ── Démo ───────────────────────────────────────────────────────────────────

let demoNextId = 100;

const demoPoll = (id: number, question: string, labels: string[], extra: Partial<Poll> = {}): Poll => ({
  id, channelId: 0, question, status: 'draft', durationSeconds: 120, showResults: false, quiz: false, onAir: false,
  createdAt: new Date().toISOString(), openedAt: null, closedAt: null, remainingSeconds: null, resultRemainingSeconds: null,
  totalVotes: 0,
  options: labels.map((label, i) => ({ id: id * 10 + i, label, correct: false, votes: 0 })),
  ...extra,
});

const demoState = (): PollsState => ({
  channel    : { id: 0, active: true, createdFromRegie: true },
  serverTime : new Date().toISOString(),
  current    : null,
  drafts     : [
    demoPoll(1, 'Faut-il encadrer les loyers dans toutes les grandes villes ?', ['Oui, partout', 'Non, laisser le marché', 'Seulement en zone tendue']),
    demoPoll(2, 'Qui vous a le plus convaincu ce soir ?', ['Claire', 'Mehdi', 'Jean-Paul', 'Personne'], { durationSeconds: 60 }),
    { ...demoPoll(3, 'En quelle année la loi Alur a-t-elle été votée ?', ['2010', '2014', '2018'], { durationSeconds: 60, quiz: true }),
      options: ['2010', '2014', '2018'].map((label, i) => ({ id: 30 + i, label, correct: i === 1, votes: 0 })) },
  ],
  finished   : [],
});

/** Des voix qui arrivent, pour voir bouger les barres (et le bandeau) en démo. */
const demoTick = (state: PollsState): PollsState => {
  const current = state.current;
  if (!current) return state;
  if (current.status === 'open') {
    const remaining = current.remainingSeconds === null ? null : Math.max(0, current.remainingSeconds - 2);
    if (remaining === 0) {
      return { ...state, current: { ...current, status: 'closed', remainingSeconds: null, resultRemainingSeconds: 30, closedAt: new Date().toISOString() } };
    }
    const weights = current.options.map((_, i) => (current.quiz ? [1, 3, 1, 1][i] : [5, 3.5, 2.4, 1][i]) ?? 1);
    const options = current.options.map((o, i) => ({ ...o, votes: o.votes + Math.round(Math.random() * 40 * weights[i]) }));
    return { ...state, current: { ...current, remainingSeconds: remaining, options, totalVotes: options.reduce((n, o) => n + o.votes, 0) } };
  }
  if (current.status === 'closed') {
    const left = Math.max(0, (current.resultRemainingSeconds ?? 0) - 2);
    if (left === 0) return { ...state, current: null, finished: [{ ...current, status: 'closed', onAir: false }, ...state.finished] };
    return { ...state, current: { ...current, resultRemainingSeconds: left } };
  }
  return state;
};

// ── Outils ─────────────────────────────────────────────────────────────────

const clock = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(Math.max(0, seconds) % 60).padStart(2, '0')}`;

const votesText = (n: number): string => `${n.toLocaleString('fr-FR')} vote${n > 1 ? 's' : ''}`;

const percents = (poll: Poll): number[] =>
  poll.options.map(o => (poll.totalVotes > 0 ? (o.votes * 100) / poll.totalVotes : 0));

const metaText = (poll: Poll): string => {
  const duration = DURATIONS.find(d => d.value === poll.durationSeconds)?.label ?? 'Sans limite';
  return `${poll.quiz ? 'Quiz · ' : ''}${poll.options.length} réponses · ${duration}`;
};

interface Draft {
  id              : number | null;
  question        : string;
  options         : { label: string; correct: boolean }[];
  durationSeconds : number | null;
  showResults     : boolean;
  quiz            : boolean;
}

const emptyDraft = (): Draft => ({
  id: null, question: '', options: [{ label: '', correct: false }, { label: '', correct: false }],
  durationSeconds: 120, showResults: false, quiz: false,
});

const draftFrom = (poll: Poll): Draft => ({
  id: poll.id, question: poll.question,
  options: poll.options.map(o => ({ label: o.label, correct: o.correct })),
  durationSeconds: poll.durationSeconds, showResults: poll.showResults, quiz: poll.quiz,
});

/** Ce qui manque au brouillon pour être enregistré, ou null. */
const draftProblem = (draft: Draft): string | null => {
  if (!draft.question.trim()) return 'Écrivez la question.';
  const filled = draft.options.filter(o => o.label.trim());
  if (filled.length < 2) return 'Il faut au moins deux réponses.';
  if (draft.quiz && filled.filter(o => o.correct).length !== 1) return 'Quiz : cochez la bonne réponse.';
  return null;
};

const readAirOnLaunch = (): boolean => {
  try { return localStorage.getItem(AIR_ON_LAUNCH_KEY) === '1'; } catch { return false; }
};

// ── Composant ──────────────────────────────────────────────────────────────

const PollsDock: React.FC<Props> = ({ adminId, channelId, createdFromRegie, overlayRef, showAlert }) => {
  const [state,       setState]       = useState<PollsState | null>(DEMO ? demoState() : null);
  const [open,        setOpen]        = useState(false);
  const [draft,       setDraft]       = useState<Draft | null>(null);
  const [busy,        setBusy]        = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [now,         setNow]         = useState(Date.now());
  const [airOnLaunch, setAirOnLaunch] = useState(readAirOnLaunch);

  // Heure locale du dernier relevé : les délais du serveur (secondes restantes)
  // se comptent à partir d'elle, sans dépendre de l'horloge de ce poste.
  const fetchedAtRef = useRef(Date.now());

  // Le bouton vit dans l'en-tête de l'onglet Régie, comme le bouton « Public ».
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const find = () => setSlot(document.getElementById('stage-dock-slot'));
    find();
    const observer = new MutationObserver(find);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const enabled = DEMO || (createdFromRegie && !!adminId && !!channelId);

  const refresh = useCallback(async () => {
    if (DEMO) {
      setState(s => (s ? demoTick(s) : s));
      fetchedAtRef.current = Date.now();
      return;
    }
    if (!adminId || !channelId) return;
    try {
      const fresh = await fetchPolls(adminId, channelId);
      fetchedAtRef.current = Date.now();
      setState(fresh);
      setError(null);
    } catch (err) {
      // 404 : admin-api pas encore déployé. Le bouton se tait ; le panneau le dit.
      setError(err instanceof PollError && err.status === 404
        ? 'Le serveur ne connaît pas encore les sondages (admin-api à déployer, migration de customer-api à jouer).'
        : (err as Error).message);
    }
  }, [adminId, channelId]);

  const current = state?.current ?? null;
  const live    = !!current;
  const onAir   = !!current?.onAir;

  // Relevé dont le délai se reprogramme selon ce qui se passe.
  const liveRef = useRef({ live, onAir });
  liveRef.current = { live, onAir };

  useEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;
    let cancelled = false;
    const tick = async () => {
      await refresh();
      if (cancelled) return;
      const { live: isLive, onAir: isOnAir } = liveRef.current;
      const delay = isOnAir ? POLL_LIVE_MS : document.hidden ? POLL_HIDDEN_MS : isLive ? POLL_LIVE_MS : POLL_IDLE_MS;
      timer = window.setTimeout(tick, delay);
    };
    void tick();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [enabled, refresh]);

  // Minuteurs du panneau et fin du résultat à l'antenne.
  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [live]);

  const elapsed       = Math.floor((now - fetchedAtRef.current) / 1000);
  const remaining     = current?.remainingSeconds       != null ? Math.max(0, current.remainingSeconds - elapsed)       : null;
  const resultLeft    = current?.resultRemainingSeconds != null ? Math.max(0, current.resultRemainingSeconds - elapsed) : null;
  const resultExpired = current?.status === 'closed' && resultLeft === 0;

  // ── Le bandeau à l'antenne ───────────────────────────────────────────────
  const shownAtRef = useRef<{ pollId: number; at: number } | null>(null);

  useEffect(() => {
    if (!current || !current.onAir || resultExpired || (current.status !== 'open' && current.status !== 'closed')) {
      overlayRef.current = null;
      shownAtRef.current = null;
      return;
    }
    if (shownAtRef.current?.pollId !== current.id) shownAtRef.current = { pollId: current.id, at: Date.now() };
    const pcts   = percents(current);
    const top    = Math.max(0, ...current.options.map(o => o.votes));
    const closed = current.status === 'closed';
    overlayRef.current = {
      pollId       : current.id,
      question     : current.question,
      quiz         : current.quiz,
      phase        : closed ? 'closed' : 'open',
      // Résultats cachés avant le vote : à l'antenne aussi, sinon l'image dirait
      // ce que le portail tait, et orienterait les votes.
      showPercents : closed || current.showResults,
      options      : current.options.map((o, i) => ({ label: o.label, pct: pcts[i], correct: o.correct, winner: top > 0 && o.votes === top })),
      totalVotes   : current.totalVotes,
      deadline     : current.remainingSeconds != null ? fetchedAtRef.current + current.remainingSeconds * 1000 : null,
      shownAt      : shownAtRef.current.at,
    };
  }, [current, resultExpired, overlayRef]);

  // Le bandeau disparaît avec le panneau (changement de chaîne, régie fermée).
  useEffect(() => () => { overlayRef.current = null; }, [overlayRef]);

  const drafts   = state?.drafts ?? [];
  const finished = state?.finished ?? [];
  const pcts     = useMemo(() => (current ? percents(current) : []), [current]);

  // ── Stream Deck (2026-10-16) ─────────────────────────────────────────────
  // Les touches Sondages du plugin Kleon : l'état part dans l'instantané du
  // pupitre (cf. streamDeckBridge.ts), les appuis reviennent par deckHandlerRef,
  // qui passe par les mêmes gestes que les boutons du panneau.
  const deckHandlerRef = useRef<((cmd: StreamDeckPollCommand) => void) | null>(null);

  useEffect(() => {
    setStreamDeckPollsHandler(cmd => deckHandlerRef.current?.(cmd));
    return () => { setStreamDeckPollsHandler(null); publishStreamDeckPolls(undefined); };
  }, []);

  useEffect(() => {
    if (!enabled || !state) {
      publishStreamDeckPolls({ available: false, busy: false, drafts: [], current: null });
      return;
    }
    const shown = current && !resultExpired && (current.status === 'open' || current.status === 'closed') ? current : null;
    // Résultats cachés aux spectateurs avant la clôture : cachés sur la touche aussi.
    const reveal = !!shown && (shown.status === 'closed' || shown.showResults) && shown.totalVotes > 0;
    const leaderIdx = shown && reveal
      ? shown.options.reduce((best, o, i) => (o.votes > shown.options[best].votes ? i : best), 0)
      : -1;
    publishStreamDeckPolls({
      available: true,
      busy,
      drafts: state.drafts.slice(0, STREAM_DECK_POLL_DRAFTS).map(p => ({ label: p.question, quiz: p.quiz })),
      current: shown && {
        question:      shown.question,
        status:        shown.status as 'open' | 'closed',
        remaining:     shown.status === 'open' ? remaining : null,
        onAir:         shown.onAir,
        totalVotes:    shown.totalVotes,
        quiz:          shown.quiz,
        leader:        leaderIdx >= 0 ? shown.options[leaderIdx].label : null,
        leaderPercent: leaderIdx >= 0 ? Math.round(percents(shown)[leaderIdx]) : null,
      },
    });
  }, [enabled, state, current, remaining, resultExpired, busy]);

  if (!enabled) return null;

  // ── Gestes ─────────────────────────────────────────────────────────────────

  /** Exécute un geste serveur (ou sa version démo), puis relit l'état. */
  const act = async (label: string, run: () => Promise<unknown>, demo: (s: PollsState) => PollsState) => {
    if (DEMO) { setState(s => (s ? demo(s) : s)); fetchedAtRef.current = Date.now(); setNow(Date.now()); return; }
    if (!adminId) return;
    setBusy(true);
    try {
      await run();
      await refresh();
    } catch (err) {
      showAlert(`${label} : ${(err as Error).message}`, 'important');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const toInput = (d: Draft): PollDraftInput => ({
    question        : d.question.trim().slice(0, QUESTION_MAX),
    options         : d.options.filter(o => o.label.trim()).map(o => ({ label: o.label.trim().slice(0, OPTION_MAX), correct: d.quiz && o.correct })),
    durationSeconds : d.durationSeconds,
    showResults     : d.showResults,
    quiz            : d.quiz,
  });

  const saveDraft = async () => {
    if (!draft) return;
    const problem = draftProblem(draft);
    if (problem) { setError(problem); return; }
    const input = toInput(draft);
    await act(
      'Le sondage n\'a pas pu être enregistré',
      () => (draft.id === null ? createPoll(adminId!, channelId!, input) : updatePoll(adminId!, draft.id, input)),
      s => {
        const poll = demoPoll(draft.id ?? demoNextId++, input.question, [], { ...input, options: input.options.map((o, i) => ({ id: i, label: o.label, correct: o.correct, votes: 0 })) });
        return { ...s, drafts: draft.id === null ? [...s.drafts, poll] : s.drafts.map(p => (p.id === draft.id ? poll : p)) };
      },
    );
    setDraft(null);
    setError(null);
  };

  const launch = (poll: Poll) => act(
    'Le sondage n\'a pas pu être lancé',
    () => openPoll(adminId!, poll.id, airOnLaunch),
    s => ({
      ...s,
      drafts  : s.drafts.filter(p => p.id !== poll.id),
      finished: s.current && s.current.status === 'closed' ? [s.current, ...s.finished] : s.finished,
      current : { ...poll, status: 'open', onAir: airOnLaunch, remainingSeconds: poll.durationSeconds, openedAt: new Date().toISOString() },
    }),
  );

  const close = (poll: Poll) => act(
    'Le sondage n\'a pas pu être clos',
    () => closePoll(adminId!, poll.id),
    s => ({ ...s, current: s.current && { ...s.current, status: 'closed', remainingSeconds: null, resultRemainingSeconds: 30 } }),
  );

  const withdraw = (poll: Poll) => act(
    'Le résultat n\'a pas pu être retiré',
    () => withdrawPoll(adminId!, poll.id),
    s => ({ ...s, current: null, finished: s.current ? [{ ...s.current, status: 'archived', onAir: false }, ...s.finished] : s.finished }),
  );

  const toggleAir = (poll: Poll) => act(
    'Le bandeau n\'a pas pu être changé',
    () => setPollOnAir(adminId!, poll.id, !poll.onAir),
    s => ({ ...s, current: s.current && { ...s.current, onAir: !poll.onAir } }),
  );

  const duplicate = (poll: Poll) => act(
    'Le sondage n\'a pas pu être repris',
    () => duplicatePoll(adminId!, poll.id),
    s => ({ ...s, drafts: [...s.drafts, { ...poll, id: demoNextId++, status: 'draft', onAir: false, totalVotes: 0, options: poll.options.map(o => ({ ...o, votes: 0 })) }] }),
  );

  const remove = (poll: Poll) => act(
    'Le sondage n\'a pas pu être supprimé',
    () => deletePoll(adminId!, poll.id),
    s => ({ ...s, drafts: s.drafts.filter(p => p.id !== poll.id), finished: s.finished.filter(p => p.id !== poll.id) }),
  );

  const changeAirOnLaunch = () => {
    const next = !airOnLaunch;
    setAirOnLaunch(next);
    try { localStorage.setItem(AIR_ON_LAUNCH_KEY, next ? '1' : '0'); } catch { /* préférence de ce poste seulement */ }
  };

  // Appuis du Stream Deck : mêmes gestes que les boutons du panneau.
  deckHandlerRef.current = (cmd: StreamDeckPollCommand) => {
    if (busy) return;
    if (cmd.action === 'launch') {
      const poll = drafts[cmd.index];
      // Un seul sondage ouvert à la fois, comme le panneau (le serveur dirait `busy`).
      if (poll && current?.status !== 'open') void launch(poll);
      return;
    }
    if (!current || resultExpired) return;
    if (cmd.action === 'onAir') { void toggleAir(current); return; }
    if (current.status === 'open') void close(current);
    else if (current.status === 'closed') void withdraw(current);
  };

  // ── Rendu ──────────────────────────────────────────────────────────────────

  const isOpenPoll = current?.status === 'open';
  const channelOff = state?.channel.active === false;

  const dockLabel = isOpenPoll
    ? `Sondage · ${remaining !== null ? clock(remaining) : 'en cours'}`
    : current?.status === 'closed' ? 'Sondage · résultat' : 'Sondages';

  const updateOption = (index: number, patch: Partial<{ label: string; correct: boolean }>) =>
    setDraft(d => d && {
      ...d,
      options: d.options.map((o, i) => (i === index
        ? { ...o, ...patch }
        : patch.correct ? { ...o, correct: false } : o)),
    });

  const inputClass = 'w-full rounded-lg border border-slate-600 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-violet-400 focus:outline-none';

  const pollCard = (poll: Poll, kind: 'draft' | 'finished') => (
    <div key={poll.id} className="rounded-xl border border-slate-700 bg-slate-900/60 p-3">
      <div className="text-sm font-semibold leading-snug">{poll.question}</div>
      <div className="mt-2 flex items-center gap-1.5">
        <span className="flex-1 text-xs text-slate-400">
          {kind === 'draft' ? metaText(poll) : `${votesText(poll.totalVotes)}${poll.quiz ? ' · quiz' : ''}`}
        </span>
        {kind === 'draft' ? (
          <>
            <button disabled={busy} onClick={() => { setDraft(draftFrom(poll)); setError(null); }} title="Modifier" aria-label="Modifier"
              className="rounded-md p-1.5 text-slate-300 hover:bg-slate-700 disabled:opacity-50"><Pencil className="h-4 w-4" /></button>
            <button disabled={busy} onClick={() => remove(poll)} title="Supprimer" aria-label="Supprimer"
              className="rounded-md p-1.5 text-slate-300 hover:bg-slate-700 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
            <button disabled={busy || isOpenPoll || channelOff} onClick={() => launch(poll)}
              title={channelOff ? 'Allumez la chaîne pour lancer un sondage' : isOpenPoll ? 'Un sondage est déjà en cours' : 'Lancer le sondage'}
              className="rounded-md bg-gradient-to-r from-blue-500 to-violet-500 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40">Lancer</button>
          </>
        ) : (
          <>
            <button disabled={busy} onClick={() => remove(poll)} title="Supprimer" aria-label="Supprimer"
              className="rounded-md p-1.5 text-slate-300 hover:bg-slate-700 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
            <button disabled={busy} onClick={() => duplicate(poll)} title="Reprendre : une copie préparée, sans voix"
              className="flex items-center gap-1 rounded-md bg-slate-700 px-2.5 py-1.5 text-xs font-semibold hover:bg-slate-600 disabled:opacity-50"><Copy className="h-3.5 w-3.5" />Reprendre</button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      {slot && createPortal(
        <button
          onClick={() => setOpen(v => !v)}
          title="Sondages en direct"
          className={`ml-2 flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 text-sm font-semibold shadow-lg transition-colors ${
            isOpenPoll
              ? 'bg-emerald-500 text-emerald-950 border-emerald-300'
              : current
                ? 'bg-amber-500 text-slate-900 border-amber-300'
                : 'bg-slate-800 text-white border-slate-600 hover:bg-slate-700'}`}
        >
          <BarChart3 className="h-4 w-4" />
          {dockLabel}
          {onAir && <span className="rounded bg-rose-600 px-1.5 text-[10px] font-extrabold tracking-wide text-white">ANTENNE</span>}
        </button>,
        slot)}

      {/* Le panneau est posé directement dans <body> : rendu ici, sous l'onglet
          Régie, il héritait d'un ancêtre qui sert de repère aux éléments
          « fixed » (transformation CSS), et se centrait sur cet ancêtre au lieu
          de l'écran — il débordait à droite, croix de fermeture hors de vue. */}
      {open && slot && createPortal(
        <div className="fixed inset-x-4 top-24 z-[60] mx-auto flex max-h-[calc(100vh-7rem)] max-w-[1120px] flex-col rounded-xl border border-slate-700 bg-slate-800 p-4 text-white shadow-2xl">
          <div className="mb-3 flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-violet-400" />
            <h3 className="flex-1 font-bold">Sondages en direct</h3>
            <button onClick={() => setOpen(false)} aria-label="Fermer" className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
          </div>

          {error && <p className="mb-3 rounded border border-orange-500/40 bg-orange-900/30 px-3 py-2 text-xs text-orange-300">{error}</p>}
          {channelOff && <p className="mb-3 rounded border border-slate-600 bg-slate-900/50 px-3 py-2 text-xs text-slate-300">La chaîne est éteinte : préparez vos sondages, ils se lanceront une fois le direct allumé.</p>}

          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto md:grid-cols-[300px_minmax(0,1fr)_minmax(0,1fr)]">
            {/* ── Préparés ─────────────────────────────────────────────── */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center">
                <h4 className="flex-1 font-semibold">Préparés</h4>
                <button onClick={() => { setDraft(emptyDraft()); setError(null); }}
                  className="flex items-center gap-1 rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-semibold hover:bg-slate-600"><Plus className="h-3.5 w-3.5" />Nouveau</button>
              </div>
              {drafts.map(p => pollCard(p, 'draft'))}
              {drafts.length === 0 && <p className="rounded-lg bg-slate-900/40 p-3 text-sm text-slate-400">Aucun sondage préparé. « Nouveau » pour en écrire un.</p>}

              {finished.length > 0 && (
                <>
                  <h4 className="mt-3 font-semibold">Terminés</h4>
                  {finished.map(p => pollCard(p, 'finished'))}
                </>
              )}
            </div>

            {/* ── Modifier le sondage ─────────────────────────────────── */}
            <div className="flex flex-col gap-3 rounded-xl border border-slate-700 bg-slate-900/40 p-4">
              {draft ? (
                <>
                  <h4 className="font-semibold">{draft.id === null ? 'Nouveau sondage' : 'Modifier le sondage'}</h4>
                  <label className="flex flex-col gap-1 text-xs text-slate-300">Question
                    <input value={draft.question} maxLength={QUESTION_MAX} placeholder="Faut-il… ?"
                      onChange={e => setDraft(d => d && { ...d, question: e.target.value })} className={inputClass} />
                  </label>

                  <div className="flex flex-col gap-1.5 text-xs text-slate-300">
                    Réponses (2 à 4){draft.quiz && ' — cochez la bonne'}
                    {draft.options.map((option, i) => (
                      <div key={i} className="flex items-center gap-2">
                        {draft.quiz && (
                          <input type="radio" name="poll-correct" checked={option.correct} aria-label={`Bonne réponse : réponse ${i + 1}`}
                            onChange={() => updateOption(i, { correct: true })} className="h-4 w-4 accent-emerald-400" />
                        )}
                        <input value={option.label} maxLength={OPTION_MAX} aria-label={`Réponse ${i + 1}`} placeholder={`Réponse ${i + 1}`}
                          onChange={e => updateOption(i, { label: e.target.value })} className={inputClass} />
                        {draft.options.length > 2 && (
                          <button onClick={() => setDraft(d => d && { ...d, options: d.options.filter((_, j) => j !== i) })}
                            aria-label={`Retirer la réponse ${i + 1}`} className="rounded p-1 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
                        )}
                      </div>
                    ))}
                    {draft.options.length < 4 && (
                      <button onClick={() => setDraft(d => d && { ...d, options: [...d.options, { label: '', correct: false }] })}
                        className="rounded-lg border border-dashed border-slate-600 py-1.5 text-xs text-slate-300 hover:border-slate-400">+ Ajouter une réponse</button>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-300">Durée
                    <div role="group" aria-label="Durée" className="flex gap-1 rounded-lg bg-slate-900/60 p-1">
                      {DURATIONS.map(d => (
                        <button key={d.label} aria-pressed={draft.durationSeconds === d.value}
                          onClick={() => setDraft(x => x && { ...x, durationSeconds: d.value })}
                          className={`rounded-md px-2.5 py-1 ${draft.durationSeconds === d.value ? 'bg-white font-bold text-slate-900' : 'text-white hover:bg-slate-700'}`}>{d.label}</button>
                      ))}
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-xs text-slate-200">
                    <input type="checkbox" checked={draft.showResults} onChange={e => setDraft(d => d && { ...d, showResults: e.target.checked })} />
                    Montrer les résultats avant le vote
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-200">
                    <input type="checkbox" checked={draft.quiz}
                      onChange={e => setDraft(d => d && { ...d, quiz: e.target.checked, options: d.options.map(o => ({ ...o, correct: false })) })} />
                    Quiz : indiquer la bonne réponse (révélée à la clôture)
                  </label>

                  <div className="mt-auto flex gap-2">
                    <button onClick={() => { setDraft(null); setError(null); }} className="flex-1 rounded-lg bg-slate-700 py-2 text-sm font-semibold hover:bg-slate-600">Annuler</button>
                    <button disabled={busy} onClick={saveDraft}
                      className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-violet-500 py-2 text-sm font-bold hover:bg-violet-400 disabled:opacity-50"><Check className="h-4 w-4" />Enregistrer</button>
                  </div>
                </>
              ) : (
                <p className="text-sm leading-relaxed text-slate-400">
                  Préparez vos questions avant le direct : 2 à 4 réponses, une durée, et le mode quiz si une réponse est la bonne.
                  Un sondage lancé ne se modifie plus ; un terminé se reprend en copie.
                </p>
              )}
            </div>

            {/* ── En cours ─────────────────────────────────────────────── */}
            <div className={`flex flex-col gap-3 rounded-xl border bg-slate-900/40 p-4 ${isOpenPoll ? 'border-emerald-400/50' : 'border-slate-700'}`}>
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${isOpenPoll ? 'bg-emerald-400' : current ? 'bg-amber-400' : 'bg-slate-600'}`} />
                <span className="flex-1 text-xs font-extrabold tracking-wider">{isOpenPoll ? 'EN COURS' : current ? 'TERMINÉ' : 'EN COURS'}</span>
                <span className="text-xs tabular-nums text-slate-300">
                  {isOpenPoll ? (remaining !== null ? clock(remaining) : 'sans limite') : current ? `résultat affiché encore ${resultLeft ?? 0} s` : ''}
                </span>
              </div>

              {current ? (
                <>
                  <div className="font-bold leading-snug">{current.question}</div>
                  {current.options.map((o, i) => {
                    const highlight = current.status === 'closed' && current.quiz && o.correct;
                    return (
                      <div key={o.id} className={`relative h-10 overflow-hidden rounded-lg bg-white/5 ${highlight ? 'ring-2 ring-emerald-400' : ''}`}>
                        <div className={`absolute inset-y-0 left-0 transition-all duration-700 ${o.correct && current.quiz ? 'bg-emerald-500/40' : 'bg-violet-500/45'}`} style={{ width: `${pcts[i]}%` }} />
                        <div className="relative flex h-10 items-center gap-2 px-3 text-sm">
                          {current.quiz && o.correct && <Check className="h-4 w-4 text-emerald-300" aria-label="Bonne réponse" />}
                          <span className="flex-1 truncate">{o.label}</span>
                          <span className="font-extrabold tabular-nums">{Math.round(pcts[i])} % · {o.votes.toLocaleString('fr-FR')}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="text-xs text-slate-400">
                    {votesText(current.totalVotes)}
                    {!current.showResults && current.status === 'open' && ' · résultats cachés aux spectateurs tant qu\'ils n\'ont pas voté'}
                  </div>

                  <div className="flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2">
                    <Radio className={`h-4 w-4 ${onAir ? 'text-rose-400' : 'text-slate-400'}`} />
                    <span className="flex-1 text-sm">Afficher à l'antenne</span>
                    <button role="switch" aria-checked={onAir} aria-label="Afficher à l'antenne" disabled={busy} onClick={() => toggleAir(current)}
                      className={`flex h-7 w-12 rounded-full p-1 transition-colors ${onAir ? 'justify-end bg-rose-600' : 'justify-start bg-slate-600'}`}>
                      <span className="h-5 w-5 rounded-full bg-white" />
                    </button>
                  </div>

                  {isOpenPoll ? (
                    <button disabled={busy} onClick={() => close(current)}
                      className="rounded-lg border border-rose-500/60 py-2.5 text-sm font-bold text-rose-300 hover:bg-rose-500/10 disabled:opacity-50">Clore le sondage</button>
                  ) : (
                    <button disabled={busy} onClick={() => withdraw(current)}
                      className="rounded-lg border border-slate-500 py-2.5 text-sm font-bold text-slate-200 hover:bg-white/5 disabled:opacity-50">Retirer le résultat</button>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm leading-relaxed text-slate-400">Aucun sondage en cours. Lancez-en un depuis la liste : il paraît aussitôt dans le lecteur de Kleon.tv, où les spectateurs connectés votent.</p>
                  <label className="flex items-center gap-2 text-xs text-slate-200">
                    <input type="checkbox" checked={airOnLaunch} onChange={changeAirOnLaunch} />
                    Afficher à l'antenne dès le lancement
                  </label>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default PollsDock;
